// filepath: frontend/src/components/AddUserModal.tsx
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserPlus, Copy, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/authStore';

interface Props {
  open: boolean;
  onClose: () => void;
}

const ACCENT = '#E47527';

const ALL_ROLES = [
  { value: 'filing_clerk',    label: 'Filing Clerk' },
  { value: 'account_manager', label: 'Account Manager' },
  { value: 'accounts',        label: 'Accounts' },
  { value: 'document_runner', label: 'Document Runner' },
  { value: 'ceo',             label: 'CEO' },
];

const AM_ROLES = [
  { value: 'filing_clerk', label: 'Filing Clerk' },
];

export function AddUserModal({ open, onClose }: Props) {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const isCeo = user?.role === 'ceo';

  const roles = isCeo ? ALL_ROLES : AM_ROLES;

  const [form, setForm] = useState({ name: '', email: '', role: roles[0].value, company: '', phone: '', tin: '' });
  const [creds, setCreds] = useState<{ name: string; email: string; temp_password: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const createUser = useMutation({
    mutationFn: (body: typeof form) => api.post('/users/invite', body).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      setCreds({ name: data.user.name, email: data.user.email, temp_password: data.temp_password });
      setForm({ name: '', email: '', role: roles[0].value, company: '', phone: '', tin: '' });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error ?? 'Failed to create user'),
  });

  const handleClose = () => {
    setCreds(null);
    setCopied(false);
    onClose();
  };

  const copyPassword = () => {
    if (creds) {
      navigator.clipboard.writeText(creds.temp_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    padding: '8px 12px',
    color: 'white',
    fontSize: 13,
    outline: 'none',
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <div className="fixed inset-0 z-50 bg-black/50" onClick={handleClose} />
          <motion.div
            className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
          >
            <div
              className="rounded-2xl p-6 shadow-2xl"
              style={{ background: '#0d1f38', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <UserPlus size={16} style={{ color: ACCENT }} />
                  <h2 className="text-sm font-semibold" style={{ color: 'white' }}>Add User</h2>
                </div>
                <button onClick={handleClose} className="p-1 hover:opacity-70 transition-opacity">
                  <X size={15} style={{ color: 'rgba(255,255,255,0.4)' }} />
                </button>
              </div>

              {creds ? (
                <div className="space-y-4">
                  <div className="rounded-xl p-4" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)' }}>
                    <p className="text-sm font-semibold mb-1" style={{ color: '#22c55e' }}>User created!</p>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                      Share these credentials with <strong style={{ color: 'white' }}>{creds.name}</strong>
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>Email</p>
                        <p className="text-sm font-mono" style={{ color: 'white' }}>{creds.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>Temp Password</p>
                        <p className="text-sm font-mono font-bold" style={{ color: ACCENT }}>{creds.temp_password}</p>
                      </div>
                      <button onClick={copyPassword} className="p-1.5 rounded hover:opacity-70 transition-opacity">
                        {copied ? <Check size={14} style={{ color: '#22c55e' }} /> : <Copy size={14} style={{ color: 'rgba(255,255,255,0.4)' }} />}
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => setCreds(null)}
                      className="flex-1 py-2 rounded-xl text-sm font-medium transition-opacity hover:opacity-80"
                      style={{ background: 'rgba(255,255,255,0.06)', color: 'white' }}
                    >
                      Add Another
                    </button>
                    <button
                      onClick={handleClose}
                      className="flex-1 py-2 rounded-xl text-sm font-medium transition-opacity hover:opacity-80"
                      style={{ background: ACCENT, color: 'white' }}
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={(e) => { e.preventDefault(); createUser.mutate(form); }}
                  className="space-y-3"
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Full Name *</label>
                      <input
                        style={inputStyle}
                        value={form.name}
                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="Jane Smith"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Email *</label>
                      <input
                        style={inputStyle}
                        type="email"
                        value={form.email}
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        placeholder="jane@company.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Role *</label>
                    <select
                      style={{ ...inputStyle, cursor: 'pointer' }}
                      value={form.role}
                      onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                    >
                      {roles.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Company</label>
                      <input
                        style={inputStyle}
                        value={form.company}
                        onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                        placeholder="Acme Ltd."
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>TIN</label>
                      <input
                        style={inputStyle}
                        value={form.tin}
                        onChange={e => setForm(f => ({ ...f, tin: e.target.value }))}
                        placeholder="Tax ID number"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase tracking-wider block mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Phone</label>
                    <input
                      style={inputStyle}
                      value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="+1 246 000 0000"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={createUser.isPending || !form.name || !form.email}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold transition-opacity hover:opacity-85 disabled:opacity-40 mt-1"
                    style={{ background: ACCENT, color: 'white' }}
                  >
                    {createUser.isPending ? 'Creating…' : 'Create User'}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
